import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { InputGroup } from '../../../shared/InputGroup/InputGroup'
import { SelectGroup } from '../../../shared/SelectGroup/SelectGroup'
import { MultiSelect } from 'react-multi-select-component'
import { ROLE_OPTIONS } from '../../../shared/Constants/Constants'
import { BlockData } from '../../../shared/Constants/blocks'
import { updateUser } from '../../../reduxLogic/reducers/users/makeUserSlice'
import { getAllProjects } from '../../../reduxLogic/reducers/projects/getAllProjectSlice'
import * as Styles from '../FarmerForm/style'
import close from '../../../assets/images/close.png'
import { getAllUsers } from '../../../reduxLogic/reducers/auth/getAllUsersSlice'

const language_options = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'mr', label: 'Marathi' },
  { value: 'bn', label: 'Bengali' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'or', label: 'Odia' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ur', label: 'Urdu' },
]

const produceOptions = [
  { value: 'wheat', label: 'Wheat' },
  { value: 'rice', label: 'Rice' },
  { value: 'cotton', label: 'Cotton' },
]

const userSchema = Yup.object().shape({
  name: Yup.string().required('User Name is required'),
})

const EditUserForm = ({ user, setOpenEditModal }) => {
  const dispatch = useDispatch()
  const { projectData } = useSelector(state => state.allProjects)

  const [states, setStates] = useState([])
  const [districts, setDistricts] = useState([])
  const [cities, setCities] = useState([])
  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedAssignedDistricts, setSelectedAssignedDistricts] = useState([])
  const [selectedAssignedBlocks, setSelectedAssignedBlocks] = useState([])

  console.log('EditUserForm user:', user)

  useEffect(() => {
    dispatch(getAllProjects())

    const uniqueStates = [...new Set(BlockData.map(item => item.state))]
    setStates(uniqueStates)
  }, [dispatch])

  useEffect(() => {
    // Set initial roles
    if (user?.userRoles?.length > 0) {
      setSelectedRoles(
        user.userRoles.map(role => {
          const match = ROLE_OPTIONS.find(opt => opt.value === role)
          return match || { label: role, value: role }
        })
      )
    }

    if (user.adminMetaInfo?.assignedDistricts) {
      setSelectedAssignedDistricts(
        user.adminMetaInfo.assignedDistricts.map(district => ({
          label: district,
          value: district,
        }))
      )
    }

    if (user.adminMetaInfo?.assignedBlocks) {
      setSelectedAssignedBlocks(
        user.adminMetaInfo.assignedBlocks.map(block => ({
          label: block,
          value: block,
        }))
      )
    }

    // Populate district and city lists based on existing data
    if (user?.address?.state) {
      const filteredDistricts = [
        ...new Set(
          BlockData.filter(item => item.state === user.address.state).map(
            item => item.district
          )
        ),
      ]
      setDistricts(filteredDistricts)
    }

    if (user?.address?.district) {
      const filteredCities = [
        ...new Set(
          BlockData.filter(item => item.district === user.address.district).map(
            item => item.city
          )
        ),
      ]
      setCities(filteredCities)
    }
  }, [user])

  const handleStateChange = (val, setFieldValue) => {
    setFieldValue('state', val)
    setFieldValue('district', '')
    setFieldValue('city', '')
    const filtered = BlockData.filter(item => item.state === val)
    setDistricts([...new Set(filtered.map(i => i.district))])
    setCities([])
  }

  const handleDistrictChange = (val, setFieldValue) => {
    setFieldValue('district', val)
    setFieldValue('city', '')
    const filtered = BlockData.filter(item => item.district === val)
    setCities([...new Set(filtered.map(i => i.city))])
  }

  const handleAssignedDistrictChange = (selectedDistricts, setFieldValue) => {
    const districtNames = selectedDistricts.map(d => d.value)
    setFieldValue('adminMetaInfo.assignedDistricts', districtNames)
  }
  
  const handleAssignedBlockChange = (selectedBlocks, setFieldValue) => {
    const blockNames = selectedBlocks.map(b => b.value)
    setFieldValue('adminMetaInfo.assignedBlocks', blockNames)
  }
  
  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      ...values,
      userRoles: selectedRoles.map(r => r.value),
      address: {
        state: values.state,
        district: values.district,
        city: values.city,
        pincode: values.pincode,
      },
    }

    dispatch(updateUser({ userInfo: payload })).then(() => {
      toast.success('User updated successfully')
      setOpenEditModal(false)
      setSelectedRoles([])
      dispatch(getAllUsers()) // Refresh user list
    })
    setSubmitting(false)
  }

  return (
    <Styles.ModalWrapper>
      <Styles.ModalContent>
        <Formik
          initialValues={{
            name: user?.name || '',
            mobileNumber: user?.mobileNumber || '',
            addressLine: user?.fulladdress || '',
            city: user.address?.city || '',
            district: user.address?.district || '',
            state: user.address?.state || '',
            pincode: user.address?.pincode || '',
            language: user.preferredLanguage || '',
            aadharNumber: user.aadharNumber || '',
            produce: user.produce || '',
            projectId: user.projectId || '',
            email: user.email || '',
            password: '',
            _id: user._id || '',
          }}
          validationSchema={userSchema}
          onSubmit={handleSubmit}
        >
          {formik => (
            <Styles.FormWithCloseButton>
              <Styles.CloseButtonDiv>
                <h3>Edit User</h3>
                <Styles.FormCloseButton onClick={() => setOpenEditModal(false)}>
                  <img src={close} alt="Close" />
                </Styles.FormCloseButton>
              </Styles.CloseButtonDiv>

              <div className="multi-select-group" style={{}}>
                <label>Select Roles</label>
                <MultiSelect
                  options={ROLE_OPTIONS}
                  value={selectedRoles}
                  onChange={setSelectedRoles}
                  hasSelectAll={false}
                />
              </div>
              <Styles.RowFlex>
                <InputGroup
                  PlaceholderText="Enter Name"
                  LabelText="Name"
                  name="name"
                  onChange={formik.handleChange}
                  defaultValue={formik.values.name}
                />
                <InputGroup
                  PlaceholderText="Email"
                  LabelText="Email"
                  name="email"
                  onChange={formik.handleChange}
                  defaultValue={formik.values.email}
                />
              </Styles.RowFlex>

              <Styles.RowFlex>
                <SelectGroup
                  PlaceholderText="State"
                  LabelText="State"
                  name="state"
                  options={states}
                  value={formik.values.state}
                  onChange={e =>
                    handleStateChange(e.target.value, formik.setFieldValue)
                  }
                />
                <SelectGroup
                  PlaceholderText="District"
                  LabelText="District"
                  name="district"
                  options={districts}
                  value={formik.values.district}
                  onChange={e =>
                    handleDistrictChange(e.target.value, formik.setFieldValue)
                  }
                />
              </Styles.RowFlex>

              <Styles.RowFlex>
                <SelectGroup
                  PlaceholderText="City"
                  LabelText="City"
                  name="city"
                  options={cities}
                  defaultValue={formik.values.city}
                  onChange={formik.handleChange}
                />
                <InputGroup
                  PlaceholderText="Enter Address"
                  LabelText="Address"
                  name="addressLine"
                  onChange={formik.handleChange}
                  defaultValue={formik.values.addressLine}
                />
              </Styles.RowFlex>

              <Styles.RowFlex>
                <InputGroup
                  PlaceholderText="Mobile Number"
                  LabelText="Mobile"
                  name="mobileNumber"
                  onChange={formik.handleChange}
                  defaultValue={formik.values.mobileNumber}
                />
                <InputGroup
                  PlaceholderText="Pincode"
                  LabelText="Pincode"
                  name="pincode"
                  onChange={formik.handleChange}
                  defaultValue={formik.values.pincode}
                />
              </Styles.RowFlex>

              <label>Preferred Language</label>
              <Select
                options={language_options}
                value={language_options.find(
                  l => l.value === formik.values.language
                )}
                onChange={e => formik.setFieldValue('language', e.value)}
              />

              {selectedRoles.some(role => role.value === 'bee_mitra') && (
                <InputGroup
                  PlaceholderText="Aadhar Number"
                  LabelText="Aadhar"
                  name="aadharNumber"
                  onChange={formik.handleChange}
                  value={formik.values.aadharNumber}
                />
              )}

              {selectedRoles.some(role => role.value === 'farmer') && (
                <div style={{ marginBottom: '1rem' }}>
                  <label>Select Produce</label>
                  <Select
                    options={produceOptions}
                    value={produceOptions.find(
                      p => p.value === formik.values.produce
                    )}
                    onChange={e => formik.setFieldValue('produce', e.value)}
                  />
                </div>
              )}

              {selectedRoles.some(role => role.value.includes('admin')) && (
                <Styles.RowFlex>
                  <div style={{ width: '100%' }}>
                    <label>Assigned Field Districts:</label>
                    <MultiSelect
                      options={districts.map(d => ({ label: d, value: d }))}
                      value={selectedAssignedDistricts}
                      onChange={selected => {
                        handleAssignedDistrictChange(
                          selected,
                          formik.setFieldValue
                        )
                        setSelectedAssignedDistricts(selected) // Update local state too
                      }}
                      hasSelectAll={false}
                      labelledBy="Assigned Field Districts"
                    />
                  </div>
                </Styles.RowFlex>
              )}

              {selectedRoles.some(role => role.value.includes('admin')) && (
                <Styles.RowFlex>
                  <div style={{ width: '100%' }}>
                    <label>Assigned Field Blocks:</label>
                    <MultiSelect
                      options={BlockData.filter(
                        item =>
                          item.state === formik.values.state &&
                          selectedAssignedDistricts
                            .map(d => d.value)
                            .includes(item.district)
                      ).map(d => ({ label: d.city, value: d.city }))}
                      value={selectedAssignedBlocks}
                      onChange={selected => {
                        handleAssignedBlockChange(
                          selected,
                          formik.setFieldValue
                        )
                        setSelectedAssignedBlocks(selected) // Update local state too
                      }}
                      hasSelectAll={false}
                      labelledBy="Assigned Field Blocks"
                    />
                  </div>
                </Styles.RowFlex>
              )}

              {selectedRoles.some(
                role =>
                  role.value === 'funder' || role.value.includes('field_view')
              ) && (
                <div style={{ marginBottom: '1rem' }}>
                  <label>Assign Project</label>
                  <Select
                    options={projectData.map(p => ({
                      label: p.name,
                      value: p._id,
                    }))}
                    value={projectData
                      .map(p => ({ label: p.name, value: p._id }))
                      .find(p => p.value === formik.values.projectId)}
                    onChange={e => formik.setFieldValue('projectId', e.value)}
                  />
                </div>
              )}

              {selectedRoles.some(role => role.value === 'funder') && (
                <>
                  <InputGroup
                    PlaceholderText="Email"
                    LabelText="Email"
                    name="email"
                    onChange={e =>
                      formik.setFieldValue('email', e.target.value)
                    }
                    value={formik.values.email}
                  />
                  <InputGroup
                    PlaceholderText="Password"
                    LabelText="Password"
                    name="password"
                    type="password"
                    onChange={e =>
                      formik.setFieldValue('password', e.target.value)
                    }
                    value={formik.values.password}
                  />
                </>
              )}

              <Styles.CreateFarmerButton type="submit">
                Update User
              </Styles.CreateFarmerButton>
            </Styles.FormWithCloseButton>
          )}
        </Formik>
      </Styles.ModalContent>
    </Styles.ModalWrapper>
  )
}

export default EditUserForm
